/**
 * The authenticated transport every screen reads through.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * WHAT THIS LAYER IS FOR
 *   One place that knows how the API answers: bearer credential in, JSON out, and the
 *   refusal document (RFC 9457 `Problem`) carried as a typed error instead of being
 *   stringified away. The screens never see `fetch`, and they never branch on a status
 *   code they have not been given a meaning for.
 *
 * THE DOCTRINE THE STATUS HANDLING ENCODES
 *   The predicate denies by absence: a record another party may not see answers 404, and
 *   so does a record that does not exist. The client therefore has a dedicated absence
 *   error and every screen renders the same absence panel for it. No screen can tell the
 *   difference, and none should be able to.
 */

import type { components } from "./schema";

/** The refusal document the API answers with. `rule` names the product rule that refused. */
export type Problem = components["schemas"]["Problem"];

/**
 * One place a request failed validation (wave W1.5): the dotted field path the form maps
 * onto its control, and what is wrong in plain words. The server never echoes the value.
 */
export type FieldProblem = components["schemas"]["FieldProblem"];

/** Why a request failed, in the only terms a screen is allowed to reason about. */
export type ApiErrorKind =
  "absence" | "forbidden" | "conflict" | "invalid" | "unavailable" | "network";

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number;
  /** The refusal document, when the server sent one. Absent for network failures. */
  readonly problem: Problem | null;

  constructor(kind: ApiErrorKind, status: number, problem: Problem | null) {
    const detail =
      problem?.detail ??
      (
        {
          absence:
            "No record came back for this. That is the same answer the server gives for a record that does not exist and one this account may not see.",
          forbidden: "The server refused this action for this account.",
          conflict: "The record is not in a state that allows this.",
          invalid: "What was sent was not valid.",
          unavailable: "The service did not answer.",
          network: "The network request did not complete.",
        } as const
      )[kind];
    super(detail);
    this.name = "ApiError";
    this.kind = kind;
    this.status = status;
    this.problem = problem;
  }
}

function kindForStatus(status: number): ApiErrorKind {
  if (status === 404) {
    return "absence";
  }
  if (status === 403) {
    return "forbidden";
  }
  if (status === 409) {
    return "conflict";
  }
  if (status === 422) {
    return "invalid";
  }
  return "unavailable";
}

/** The per-field places of a 422, field by field; anything malformed is dropped. */
function readFieldProblems(value: unknown): FieldProblem[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((entry: unknown) => {
    if (typeof entry !== "object" || entry === null) {
      return [];
    }
    const one = entry as Record<string, unknown>;
    if (typeof one.field !== "string" || typeof one.message !== "string") {
      return [];
    }
    return [
      {
        location: typeof one.location === "string" ? one.location : "body",
        field: one.field,
        code: typeof one.code === "string" ? one.code : "",
        message: one.message,
      },
    ];
  });
}

async function readProblem(response: Response): Promise<Problem | null> {
  try {
    const body: unknown = await response.json();
    if (typeof body !== "object" || body === null) {
      return null;
    }
    const candidate = body as Partial<Problem>;
    if (typeof candidate.title === "string" && typeof candidate.status === "number") {
      const problem: Problem = {
        type: candidate.type ?? "about:blank",
        title: candidate.title,
        status: candidate.status,
        detail: typeof candidate.detail === "string" ? candidate.detail : "",
        rule: typeof candidate.rule === "string" ? candidate.rule : "",
      };
      const errors = readFieldProblems(candidate.errors);
      return errors.length > 0 ? { ...problem, errors } : problem;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Build a query string from the parameters that are actually present. A parameter the
 * caller leaves undefined is absent from the URL rather than sent empty, which is the
 * difference between "filter me by nothing" and "filter me by the empty string".
 */
export function query(
  parameters: Record<string, string | number | boolean | null | undefined>,
): string {
  const search = new URLSearchParams();
  for (const [name, value] of Object.entries(parameters)) {
    if (value === null || value === undefined) {
      continue;
    }
    search.set(name, String(value));
  }
  const encoded = search.toString();
  return encoded.length === 0 ? "" : `?${encoded}`;
}

/**
 * One authenticated call. `parse` narrows the body field by field; a body that does not
 * match is a failed request, not a screen full of undefined.
 */
export async function apiJson<T>(
  accessToken: string,
  path: string,
  parse: (body: unknown) => T,
  init?: { method?: string | undefined; body?: unknown; signal?: AbortSignal | undefined },
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method: init?.method ?? "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
        ...(init?.body === undefined ? {} : { "Content-Type": "application/json" }),
      },
      body: init?.body === undefined ? null : JSON.stringify(init.body),
      signal: init?.signal ?? null,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }
    throw new ApiError("network", 0, null);
  }

  if (!response.ok) {
    throw new ApiError(
      kindForStatus(response.status),
      response.status,
      await readProblem(response),
    );
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return parse((await response.json()) as unknown);
}

/** A multipart upload, which cannot go through the JSON path. */
export async function apiUpload<T>(
  accessToken: string,
  path: string,
  file: File,
  parse: (body: unknown) => T,
  signal?: AbortSignal,
): Promise<T> {
  const form = new FormData();
  form.set("file", file, file.name);
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}`, Accept: "application/json" },
      body: form,
      signal: signal ?? null,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }
    throw new ApiError("network", 0, null);
  }
  if (!response.ok) {
    throw new ApiError(
      kindForStatus(response.status),
      response.status,
      await readProblem(response),
    );
  }
  return parse((await response.json()) as unknown);
}

/* ---------------------------------------------------------------------- */
/* Field-by-field readers. These exist so the resource modules state the   */
/* shape they were promised rather than casting the body into it.          */
/* ---------------------------------------------------------------------- */

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function str(value: unknown): string {
  if (typeof value !== "string") {
    throw new Error("Expected a string field");
  }
  return value;
}

export function optStr(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value !== "string") {
    throw new Error("Expected a nullable string field");
  }
  return value;
}

export function num(value: unknown): number {
  if (typeof value !== "number") {
    throw new Error("Expected a number field");
  }
  return value;
}

export function optNum(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  return num(value);
}

export function bool(value: unknown): boolean {
  if (typeof value !== "boolean") {
    throw new Error("Expected a boolean field");
  }
  return value;
}

export function optBool(value: unknown): boolean | null {
  if (value === null || value === undefined) {
    return null;
  }
  return bool(value);
}

export function strArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    throw new Error("Expected an array field");
  }
  return value.map(str);
}

/** A list body: the register's items and the total behind the same filter. */
export function listBody(body: unknown): { total: number; items: Record<string, unknown>[] } {
  if (!isRecord(body)) {
    throw new Error("Expected a list body");
  }
  const items = body.items;
  if (!Array.isArray(items)) {
    throw new Error("Expected a list body with items");
  }
  return { total: num(body.total), items: items.filter(isRecord) };
}
